import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { supabase } from "@/lib/supabase-client";

interface RestrictionRef {
  id: string;
  name: string;
  description?: string;
}

type RestrictionValue = { restriction: RestrictionRef; expires?: string | null };

interface RestrictionsInputProps {
  /** Entity's own restrictions — array of { restriction: { id, name, description? }, expires? } */
  value: RestrictionValue[];
  /** Inherited restrictions — each item can carry its own sourceLabel */
  inherited?: { restriction: RestrictionRef; sourceLabel?: string }[];
  /** Default label for inherited badges (used when item has no sourceLabel) */
  inheritedLabel?: string;
  /** Show expiry date support on chips (only for user screens). Default: false */
  showExpiry?: boolean;
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (restrictions: RestrictionValue[]) => void;
}

export function RestrictionsInput({
  value,
  inherited = [],
  inheritedLabel = "From Client",
  showExpiry = false,
  state = "editing",
  label = "Restrictions",
  error,
  onChange,
}: RestrictionsInputProps) {
  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all restriction options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("restrictions")
          .select("id, name, description")
          .order("name", { ascending: true });
        if (fetchError) throw fetchError;
        if (cancelled) return;
        setOptions(
          (data ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            description: r.description || undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load restrictions:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items: inherited (readonly) + project's own (removable)
  const chipItems = useMemo<ChipItem[]>(() => {
    const chips: ChipItem[] = [];
    for (const cr of inherited) {
      chips.push({
        id: `inherited-${cr.restriction.id}`,
        name: cr.restriction.name,
        sourceLabel: cr.sourceLabel || inheritedLabel,
        readonly: true,
      });
    }
    for (const pr of value) {
      // Skip if already shown as inherited
      if (inherited.some((cr) => cr.restriction.id === pr.restriction.id)) continue;
      chips.push({
        id: pr.restriction.id,
        name: pr.restriction.name,
        subtitle: pr.restriction.description,
        readonly: false,
        ...(showExpiry ? { expiry: pr.expires ?? null } : {}),
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inherited, inheritedLabel]);

  const handleAdd = useCallback(
    (restrictionId: string) => {
      if (value.some((pr) => pr.restriction.id === restrictionId)) return;
      const opt = options.find((o) => o.id === restrictionId);
      if (!opt) return;
      onChange?.([...value, { restriction: { id: opt.id, name: opt.name, description: opt.description } }]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (chipId: string) => {
      onChange?.(value.filter((pr) => pr.restriction.id !== chipId));
    },
    [value, onChange],
  );

  const handleExpiryChange = useCallback(
    (chipId: string, date: string | null) => {
      onChange?.(value.map((pr) => (pr.restriction.id === chipId ? { ...pr, expires: date } : pr)));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="shield-alert-outline"
      accentTone="red"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search restrictions…"
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onExpiryChange={showExpiry ? handleExpiryChange : undefined}
    />
  );
}
